// src/components/ui/SessionSelector/SessionSelector.jsx
// Single-field cashier-session picker: its panel holds a date range + cashier filter that narrow the session list.
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X, Search } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getSessions, getSessionCashiers } from '../../../services/api/offlineSales/cashierSessions';
import { formatDate } from '../../../utils/dateUtils';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import MultiSelectFilter from '../../common/MultiSelectFilter/MultiSelectFilter';
import dd from '../Dropdown/Dropdown.module.css';
import styles from './SessionSelector.module.css';

const PANEL_MIN_WIDTH = 460;

const sessionLabel = (s) =>
  `${formatDate(s.openedAt)} · ${s.cashier?.name || '—'}${s.Shop?.name ? ` (${s.Shop.name})` : ''}`;

const SessionSelector = ({ value, onChange, placeholder }) => {
  const { t } = useLocalization();
  const [open, setOpen]             = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const [range, setRange]           = useState({ from: '', to: '' });
  const [cashierIds, setCashierIds] = useState([]);
  const [cashiers, setCashiers]     = useState([]);
  const [sessions, setSessions]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const wrapperRef = useRef(null);
  const panelRef   = useRef(null);

  useEffect(() => {
    getSessionCashiers().then(r => setCashiers(r?.users || [])).catch(() => {});
  }, []);

  // Cleared externally (e.g. FilterPanel "Clear")
  useEffect(() => {
    if (!value) setSelectedLabel('');
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) { setSearchTerm(''); return; }
    const updatePosition = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, PANEL_MIN_WIDTH), window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setPanelStyle({ position: 'fixed', left, width, top: rect.bottom + 6 });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!range.from || !range.to) { setSessions([]); return; }
    let cancelled = false;
    setLoading(true);
    getSessions({
      from: range.from,
      to: `${range.to}T23:59:59.999`,
      cashierIds: cashierIds.length > 0 ? cashierIds.join(',') : undefined,
      limit: 200,
    })
      .then(r => { if (!cancelled) setSessions(r?.sessions || []); })
      .catch(() => { if (!cancelled) setSessions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range.from, range.to, cashierIds]);

  const hasRange = Boolean(range.from && range.to);
  const visibleSessions = searchTerm
    ? sessions.filter(s => sessionLabel(s).toLowerCase().includes(searchTerm.toLowerCase()))
    : sessions;

  const handleSelect = (session) => {
    setSelectedLabel(sessionLabel(session));
    onChange(session.id);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    onChange('');
  };

  const hasValue = Boolean(value && selectedLabel);

  return (
    <div ref={wrapperRef} className={dd.wrapper}>
      <div
        className={`${dd.trigger} ${open ? dd.triggerOpen : ''}`}
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
      >
        <span className={`${dd.triggerText} ${!hasValue ? dd.placeholder : ''}`}>
          {hasValue ? selectedLabel : (placeholder || t('offlineSales.selectSession'))}
        </span>
        {hasValue && (
          <button type="button" className={dd.clearButton} onClick={handleClear} title="Clear">
            <X size={14} />
          </button>
        )}
        <ChevronDown className={dd.chevronIcon} size={16} />
      </div>

      {open && panelStyle && createPortal(
        <div ref={panelRef} className={`${dd.dropdown} ${styles.panel}`} style={panelStyle}>
          <div className={styles.filtersRow}>
            <div className={styles.filterCell}>
              <DateRangePicker
                value={range}
                onChange={({ from, to }) => setRange({ from: from || '', to: to || '' })}
              />
            </div>
            <div className={styles.filterCell}>
              <MultiSelectFilter
                options={cashiers}
                selectedValues={cashierIds}
                onChange={setCashierIds}
                placeholder={t('offlineSales.allCashiers')}
                displayKey="name"
                valueKey="id"
                searchKey="name"
                floatingDropdown
              />
            </div>
          </div>

          {hasRange && (
            <div className={dd.searchWrapper}>
              <Search className={dd.searchIcon} size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('offlineSales.searchSessions')}
                className={dd.searchInput}
              />
              {searchTerm && (
                <button type="button" className={dd.clearSearch} onClick={() => setSearchTerm('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          <div className={dd.optionsList}>
            {!hasRange ? (
              <div className={dd.emptyState}>{t('offlineSales.selectDateRangeFirst')}</div>
            ) : loading ? (
              <div className={dd.emptyState}>...</div>
            ) : visibleSessions.length > 0 ? (
              visibleSessions.map((s) => {
                const isSelected = String(s.id) === String(value);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`${dd.option} ${isSelected ? dd.optionSelected : ''}`}
                    onClick={() => handleSelect(s)}
                  >
                    <span className={dd.optionLabel}>{sessionLabel(s)}</span>
                    {isSelected && <Check size={14} className={dd.checkIcon} />}
                  </button>
                );
              })
            ) : (
              <div className={dd.emptyState}>{t('offlineSales.noSessionsFound')}</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SessionSelector;
